import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperCountries() {
  const [countries, setCountries] = useState([]);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchCountries = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/countries`);
      setCountries(r.data || []);
    } catch (e) {
      toast.error('فشل تحميل الدول');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCountries(); }, []);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/super/countries`, { name });
      toast.success(`تم إضافة «${name}»`);
      setNewName('');
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الإضافة');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`حذف الدولة «${c.name}» ؟`)) return;
    try {
      await axios.delete(`${API_URL}/api/super/countries/${c.id}`);
      toast.success('تم الحذف');
      fetchCountries();
    } catch (e) {
      toast.error('فشل الحذف');
    }
  };

  if (loading) return <div className="text-center py-12">جاري التحميل...</div>;

  return (
    <div className="space-y-6" dir="rtl" data-testid="super-countries-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
          <Globe className="h-6 w-6 text-brand_blue" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-primary">إدارة الدول</h1>
          <p className="text-sm text-foreground/60">
            هذه الدول تظهر في قائمة الاختيار عند تسجيل مبيعات المنتجات الدولية.
          </p>
        </div>
      </div>

      <Card className="p-4 sm:p-6">
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 w-full">
            <Label className="text-xs font-bold mb-1 block">اسم الدولة</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="مثال: ماليزيا"
              className="text-right"
              data-testid="new-country-input"
            />
          </div>
          <Button
            type="submit"
            disabled={adding || !newName.trim()}
            className="gap-2 w-full sm:w-auto"
            data-testid="add-country-btn"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'جاري الإضافة...' : 'إضافة'}
          </Button>
        </form>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-accent/30">
          <h3 className="font-black font-numbers">قائمة الدول ({countries.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-right py-3 px-4 font-bold text-sm">#</th>
                <th className="text-right py-3 px-4 font-bold text-sm">اسم الدولة</th>
                <th className="text-right py-3 px-4 font-bold text-sm">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {countries.length === 0 ? (
                <tr><td colSpan={3} className="text-center py-12 text-muted-foreground">لا توجد دول</td></tr>
              ) : countries.map((c, i) => (
                <tr key={c.id} className="border-t border-border hover:bg-accent/30" data-testid={`country-row-${c.id}`}>
                  <td className="py-3 px-4 font-numbers text-muted-foreground">{i + 1}</td>
                  <td className="py-3 px-4 font-bold">{c.name}</td>
                  <td className="py-3 px-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(c)}
                      data-testid={`delete-country-${c.id}`}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="حذف"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
